import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Edit,
  AlertTriangle,
  FileText,
  Users,
  Building,
  MapPin,
  Upload,
  Bot,
  FileBarChart,
  Check,
  Loader2,
  Mail,
  Phone,
  User,
  List,
  Eye,
  Download,
  ShieldCheck,
} from "lucide-react";
import { dealService } from "@/services/dealService";
import {
  Deal,
  DEAL_STATUS_LABELS,
  DEAL_TYPE_LABELS,
} from "@/types/deal";
import { getCurrencySymbol } from "@/components/ui/currency-select";
import { getDealFieldConfig } from "@/config/dealFieldConfig";
import { useAuth } from "@/contexts/AuthContext";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ViewDeal = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  // ---- ROLE FLAGS ----
  const globalRole = user?.role; // "ADMIN" | "ANALYST" | "REVIEWER" | "VIEWER"
  const assignedPersonas =
    deal?.dealPersonas?.filter((dp) => dp.userId === user?.id) || [];

  const assignedPersonaRoles = assignedPersonas.map((dp) => dp.role);
  const isAssignedAnalyst = assignedPersonaRoles.includes("ANALYST");
  const isAssignedReviewer = assignedPersonaRoles.includes("REVIEWER");
  const isAssignedViewer = assignedPersonaRoles.includes("VIEWER");

  const isGlobalAnalyst = globalRole === "ANALYST";
  const isDealAnalyst = isGlobalAnalyst || isAssignedAnalyst;
  const isAdminOrAnalyst = !!(isAdmin || isDealAnalyst);

  useEffect(() => {
    if (!dealId) {
      navigate("/deals");
      return;
    }
    void loadDeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, navigate]);

  const loadDeal = async () => {
    try {
      setLoading(true);
      const response = await dealService.getDealById(dealId!);
      setDeal(response);
    } catch (error) {
      console.error("Failed to load deal:", error);
      toast({
        title: "Error",
        description: "Failed to load deal details. Please try again.",
        variant: "destructive",
      });
      navigate("/deals");
    } finally {
      setLoading(false);
    }
  };

const exportRequisitionListToPDF = () => {
  if (!deal?.customRequisitionData?.categories) {
    toast({
      title: "No Data",
      description: "No requisition data available to export.",
      variant: "destructive",
    });
    return;
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Requisition List', pageWidth / 2, 20, { align: 'center' });
  
  // Deal Title
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Deal: ${deal.title}`, 14, 30);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 36);
  
  let yPosition = 45;

  // Loop through categories
  deal.customRequisitionData.categories.forEach((category, categoryIndex) => {
    // Check if we need a new page
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    // Category Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246); // Primary color
    doc.text(category.title || `Category ${categoryIndex + 1}`, 14, yPosition);
    yPosition += 8;

    if (Array.isArray(category.items) && category.items.length > 0) {
      // Prepare table data
      const tableData = category.items.map((rawItem: any, index: number) => {
        const item = typeof rawItem === "string" ? { name: rawItem } : rawItem;
        const { name, description } = item || {};
        
        return [
          (index + 1).toString(),
          name || "Untitled item",
          description || "-"
        ];
      });

      // Add table
      autoTable(doc, {
        startY: yPosition,
        head: [['#', 'Document Name', 'Description']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 4
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 70 },
          2: { cellWidth: 95 }
        },
        margin: { left: 14, right: 14 }
      });

      // Update yPosition after table
      yPosition = (doc as any).lastAutoTable.finalY + 15;
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(128);
      doc.text('No items specified for this category.', 14, yPosition);
      yPosition += 15;
    }
  });

  // Save the PDF
  const fileName = `Requisition_List_${deal.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().getTime()}.pdf`;
  doc.save(fileName);

  toast({
    title: "Success",
    description: "Requisition list exported successfully!",
  });
};

  const getStatusColor = (status: string) =>
    dealService.getStatusColor(status as any);

  const getRiskLevel = (d: Deal) => dealService.getRiskLevel(d);

  const getRiskColor = (riskLevel: string) => {
    switch ((riskLevel || "").toUpperCase()) {
      case "HIGH":
        return "text-red-600";
      case "MEDIUM":
        return "text-yellow-600";
      case "LOW":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  };

  const hasCompletedReport = (d: Deal) =>
    Boolean(d._count?.reports && d._count.reports > 0);

  const fmtMoney = (cur?: string | null, val?: any) => {
    if (val === null || val === undefined || val === "") return "Not specified";
    return `${getCurrencySymbol(cur || "INR")}${dealService.formatDealValue(val)}`;
  };

  const fmtText = (val?: any) =>
    val === null || val === undefined || val === "" ? "Not specified" : String(val);

  const formatFieldValue = (field: string, value: any, d: Deal) => {
    if (value === null || value === undefined || value === "") return "Not specified";

    const fieldConfig = getDealFieldConfig(d.type) as any;
    const specificFieldConfig = fieldConfig?.specific?.[field];

    if (specificFieldConfig?.options) {
      const option = specificFieldConfig.options.find((opt: any) => opt.value === value);
      if (option) return option.label;
    }

    switch (field) {
      case "synergiesExpected":
      case "boardRights":
        return value === true || value === "true" ? "Yes" : value === false || value === "false" ? "No" : String(value);
      case "interestRate":
      case "equityStake":
        return `${value}%`;
      case "preMoneyValuation":
      case "postMoneyValuation":
        return fmtMoney(d.currency, value);
      case "maturityDate":
        return dealService.formatDate(value);
      default:
        return String(value);
    }
  };

  const renderDealTypeSpecificFields = () => {
    if (!deal) return null;

    const fieldConfig = getDealFieldConfig(deal.type) as any;
    const specificFields = fieldConfig?.specific || {};

    const hasSpecificFieldValues = Object.keys(specificFields).some((fieldKey) => {
      const value = (deal as any)[fieldKey];
      return value !== undefined && value !== null && value !== "";
    });

    if (!hasSpecificFieldValues) return null;

    return (
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-xl">
            <FileBarChart className="w-5 h-5" />
            {DEAL_TYPE_LABELS[deal.type as keyof typeof DEAL_TYPE_LABELS]} Specific Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(specificFields).map(([fieldKey, cfg]: [string, any]) => {
              const value = (deal as any)[fieldKey];
              if (value === null || value === undefined || value === "") return null;

              return (
                <div
                  key={fieldKey}
                  className="rounded-lg border bg-card/50 p-4 transition-transform duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md hover:border-primary/30 will-change-transform"
                >
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {cfg?.label ?? fieldKey}
                  </label>
                  <p className="font-medium mt-1">{formatFieldValue(fieldKey, value, deal)}</p>
                  {cfg?.description && (
                    <p className="text-xs text-muted-foreground mt-1">{cfg.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const getCurrentStep = (d: Deal) => {
    if (hasCompletedReport(d)) return 3;
    if (d.status === "DRAFT") return 1;
    if (d.status === "ACTIVE") return 1;
    if (d.status === "DOCUMENTS_SUBMITTED") return 2;
    if (d.status === "UNDER_REVIEW" || d.status === "PENDING_APPROVAL") return 2;
    if (d.status === "COMPLETED") return 3;
    return 1;
  };

  const calculateStepProgress = (d: Deal) => {
    const step = getCurrentStep(d);
    switch (step) {
      case 1: {
        const hasDocuments = d._count?.documents && d._count.documents > 0;
        if (d.status === "DRAFT") return 0;
        if (d.status === "ACTIVE" && hasDocuments) return 25;
        if (d.status === "ACTIVE" && !hasDocuments) return 10;
        return 33;
      }
      case 2: {
        if (d.status === "DOCUMENTS_SUBMITTED") return 40;
        if (d.status === "UNDER_REVIEW") return 55;
        if (d.status === "PENDING_APPROVAL") return 65;
        return 66;
      }
      case 3: {
        if (hasCompletedReport(d)) return 100;
        if (d.status === "COMPLETED") return 100;
        return 75;
      }
      default:
        return 0;
    }
  };

  const getRoleBadgeClass = (role?: string) => {
    switch (role) {
      case "ADMIN":
        return "bg-purple-100 text-purple-700 border-purple-200";
      case "ANALYST":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "REVIEWER":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "VIEWER":
        return "bg-slate-100 text-slate-700 border-slate-200";
      default:
        return "bg-muted text-foreground border-muted";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="text-lg">Loading deal details...</span>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Deal not found</h2>
          <p className="text-muted-foreground mb-4">
            The deal you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/deals")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Deals
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/deals")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Deals
            </Button>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">{deal.title}</h1>
              <p className="text-muted-foreground">Deal Details and Overview</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link to={`/register-deal?dealId=${deal.id}`}>
                <Button className="flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Edit Deal
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Meta Badges */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`${getStatusColor(deal.status)} px-3 py-1`}>
            {DEAL_STATUS_LABELS[deal.status as keyof typeof DEAL_STATUS_LABELS]}
          </Badge>
          <Badge variant="outline" className="px-3 py-1">
            {DEAL_TYPE_LABELS[deal.type as keyof typeof DEAL_TYPE_LABELS]}
          </Badge>
          <Badge className={`px-3 py-1 ${getRiskColor(getRiskLevel(deal))}`} variant="outline">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {getRiskLevel(deal)} Risk
          </Badge>
          {deal.confidentialityLevel && (
            <Badge variant="secondary" className="px-3 py-1">
              {deal.confidentialityLevel}
            </Badge>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className={`grid w-full ${isAdmin ? "grid-cols-3" : "grid-cols-2"}`}>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="requisition" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Requisition List
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="assignees" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Assignees
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-6">
            {/* --- your overview content unchanged --- */}
            {/* (same as you pasted) */}
            {/* START overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Basic Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Deal Title</label>
                        <p className="font-medium">{deal.title}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Deal Type</label>
                        <p className="font-medium">
                          {DEAL_TYPE_LABELS[deal.type as keyof typeof DEAL_TYPE_LABELS]}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Deal Value</label>
                        <p className="font-medium text-xl">
                          {getCurrencySymbol(deal.currency)}
                          {dealService.formatDealValue(deal.value)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Expected Closing Date</label>
                        <p className="font-medium">{dealService.formatDate(deal.expectedClosingDate)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Buyer</label>
                        <p className="font-medium flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {deal.buyer || "Not specified"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Seller</label>
                        <p className="font-medium flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          {deal.seller || "Not specified"}
                        </p>
                      </div>
                      {deal.industry && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Industry</label>
                          <p className="font-medium">{deal.industry}</p>
                        </div>
                      )}
                      {deal.jurisdiction && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Buyer Jurisdiction</label>
                          <p className="font-medium flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {deal.jurisdiction}
                          </p>
                        </div>
                      )}
                      {deal.riskLevel && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Risk Level</label>
                          <p className={`font-medium flex items-center gap-2 ${getRiskColor(deal.riskLevel)}`}>
                            <AlertTriangle className="w-4 h-4" />
                            {deal.riskLevel}
                          </p>
                        </div>
                      )}
                      {deal.confidentialityLevel && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Confidentiality Level</label>
                          <p className="font-medium">{deal.confidentialityLevel}</p>
                        </div>
                      )}
                      {deal.organization && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Organization</label>
                          <p className="font-medium">{deal.organization.name}</p>
                        </div>
                      )}
                    </div>
                    {deal.description && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Description</label>
                        <p className="font-light mt-1">{deal.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {renderDealTypeSpecificFields()}

                {/* Selected Personas */}
                {Array.isArray(deal.selectedPersonasWithNames) &&
                  deal.selectedPersonasWithNames.length > 0 && (
                    <Card className="border-border/60 shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <Bot className="w-5 h-5" />
                          Selected Personas ({deal.selectedPersonasWithNames.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {deal.selectedPersonasWithNames.map((persona, index) => (
                            <div key={index} className="border rounded-lg p-3">
                              <div className="flex items-center gap-2">
                                <Bot className="w-4 h-4 text-blue-500" />
                                <div>
                                  <p className="font-medium text-sm">{persona.name}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                {/* Parties */}
                {Array.isArray(deal.parties) && deal.parties.length > 0 && (
                  <Card className="border-border/60 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Users className="w-5 h-5" />
                        Parties ({deal.parties.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {deal.parties.map((party, index) => (
                          <div key={index} className="border rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="text-xs uppercase tracking-wide text-muted-foreground">Name</label>
                                <p className="font-medium mt-1">{fmtText(party.name)}</p>
                              </div>
                              <div>
                                <label className="text-xs uppercase tracking-wide text-muted-foreground">Type</label>
                                <p className="font-medium mt-1">{fmtText(party.partyType)}</p>
                              </div>
                              {party.role && (
                                <div>
                                  <label className="text-sm font-medium text-muted-foreground">Role</label>
                                  <p className="font-medium">{party.role}</p>
                                </div>
                              )}
                            </div>
                            {party.contactInfo && (
                              <div className="mt-3">
                                <label className="text-sm font-medium text-muted-foreground">
                                  Contact Information
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1 text-sm">
                                  {party.contactInfo.email && (
                                    <p className="flex items-center gap-2">
                                      <Mail className="w-3 h-3" />
                                      {party.contactInfo.email}
                                    </p>
                                  )}
                                  {party.contactInfo.phone && (
                                    <p className="flex items-center gap-2">
                                      <Phone className="w-3 h-3" />
                                      {party.contactInfo.phone}
                                    </p>
                                  )}
                                  {party.contactInfo.address && (
                                    <p className="flex items-center gap-2">
                                      <MapPin className="w-3 h-3" />
                                      {party.contactInfo.address}
                                    </p>
                                  )}
                                  {party.contactInfo.contactPerson && (
                                    <p className="flex items-center gap-2">
                                      <User className="w-3 h-3" />
                                      {party.contactInfo.contactPerson}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {deal.suppliers && deal.suppliers.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building className="w-5 h-5" />
                        Suppliers ({deal.suppliers.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {deal.suppliers.map((supplier, index) => (
                          <div key={index} className="border rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="text-sm font-medium text-muted-foreground">Name</label>
                                <p className="font-medium">{supplier.name}</p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-muted-foreground">Seller Jurisdiction</label>
                                <p className="font-medium flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  {supplier.jurisdiction}
                                </p>
                              </div>
                              {supplier.contactInfo && (
                                <div>
                                  <label className="text-sm font-medium text-muted-foreground">Contact Info</label>
                                  <div className="text-sm space-y-1">
                                    {supplier.contactInfo.email && (
                                      <p className="flex items-center gap-2">
                                        <Mail className="w-3 h-3" />
                                        {supplier.contactInfo.email}
                                      </p>
                                    )}
                                    {supplier.contactInfo.phone && (
                                      <p className="flex items-center gap-2">
                                        <Phone className="w-3 h-3" />
                                        {supplier.contactInfo.phone}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            {supplier.description && (
                              <div className="mt-3">
                                <label className="text-sm font-medium text-muted-foreground">Description</label>
                                <p className="font-light text-sm mt-1">{supplier.description}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {deal.notes && deal.notes.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Notes ({deal.notes.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {deal.notes.map((note, index) => (
                          <div key={index} className="border-l-4 border-primary pl-4 py-2">
                            <p className="font-light">{note.note}</p>
                            <div className="text-xs text-muted-foreground mt-1">
                              By {note.user.firstName} {note.user.lastName} on{" "}
                              {dealService.formatDate(note.createdAt)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="space-y-6">
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle>Progress</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-semibold mb-2">
                        {calculateStepProgress(deal)}%
                      </div>
                      <Progress value={calculateStepProgress(deal)} className="w-full" />
                    </div>

                    <div className="space-y-1">
                      <div className="text-sm font-medium text-muted-foreground mb-3">
                        Step {getCurrentStep(deal)} of 3
                      </div>

                      {/* Vertical Stepper */}
                      <div className="relative">
                        {/* Step 1: Documents */}
                        <div className="flex items-start gap-4 pb-8 relative">
                          {/* Vertical Line */}
                          <div className="absolute left-4 top-8 w-0.5 h-16 bg-border"></div>

                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 bg-background relative z-10 ${
                              getCurrentStep(deal) >= 1
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background text-muted-foreground border-muted"
                            }`}
                          >
                            {getCurrentStep(deal) > 1 ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                          </div>

                          <div className="flex-1 pt-0.5">
                            <div className={`font-medium ${getCurrentStep(deal) >= 1 ? "text-primary" : "text-muted-foreground"}`}>
                              {getCurrentStep(deal) >= 1 && isAdminOrAnalyst ? (
                                <button
                                  onClick={() => navigate(`/document-upload?dealId=${deal.id}`)}
                                  className="text-left hover:underline focus:outline-none focus:underline transition-all"
                                >
                                  Documents
                                </button>
                              ) : (
                                "Documents"
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {deal._count?.documents || 0} uploaded
                            </div>
                          </div>
                        </div>

                        {/* Step 2: AI Review */}
                        <div className="flex items-start gap-4 pb-8 relative">
                          {/* Vertical Line */}
                          <div className="absolute left-4 top-8 w-0.5 h-16 bg-border"></div>

                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 bg-background relative z-10 ${
                              getCurrentStep(deal) >= 2
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background text-muted-foreground border-muted"
                            }`}
                          >
                            {getCurrentStep(deal) > 2 ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Bot className="w-4 h-4" />
                            )}
                          </div>

                          <div className="flex-1 pt-0.5">
                            <div className={`font-medium ${getCurrentStep(deal) >= 2 ? "text-primary" : "text-muted-foreground"}`}>
                              {getCurrentStep(deal) >= 2 ? (
                                <button
                                  onClick={() => navigate(`/ai-review?dealId=${deal.id}`)}
                                  className="text-left hover:underline focus:outline-none focus:underline transition-all"
                                >
                                  AI Review
                                </button>
                              ) : (
                                "AI Review"
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {getCurrentStep(deal) >= 2 ? "In progress" : "Pending"}
                            </div>
                          </div>
                        </div>

                        {/* Step 3: Report */}
                        <div className="flex items-start gap-4 relative">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 bg-background relative z-10 ${
                              getCurrentStep(deal) >= 3
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background text-muted-foreground border-muted"
                            }`}
                          >
                            {hasCompletedReport(deal) ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <FileBarChart className="w-4 h-4" />
                            )}
                          </div>

                          <div className="flex-1 pt-0.5">
                            <div className={`font-medium ${getCurrentStep(deal) >= 3 ? "text-primary" : "text-muted-foreground"}`}>
                              {getCurrentStep(deal) >= 3 ? (
                                <button
                                  onClick={() => navigate(`/report-generation?dealId=${deal.id}`)}
                                  className="text-left hover:underline focus:outline-none focus:underline transition-all"
                                >
                                  Report
                                </button>
                              ) : (
                                "Report"
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {hasCompletedReport(deal) ? "Completed" : "Pending"}
                            </div>

                            {hasCompletedReport(deal) && (
                              <div className="text-xs text-green-600 font-medium mt-1">
                                ✓ Completed
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle>Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    {getCurrentStep(deal) === 1 && isAdminOrAnalyst && (
                      <Link to={`/document-upload?dealId=${deal.id}`} className="block">
                        <Button size="sm" className="w-full">
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Documents
                        </Button>
                      </Link>
                    )}
                    {getCurrentStep(deal) === 2 && (
                      <Link to={`/ai-review?dealId=${deal.id}`} className="block">
                        <Button size="sm" className="w-full">
                          <Bot className="w-4 h-4 mr-2" />
                          {isAdminOrAnalyst
                            ? "Start AI Review"
                            : isAssignedReviewer
                            ? "Review AI Review"
                            : isAssignedViewer
                            ? "View AI Review"
                            : "View AI Review"}
                        </Button>
                      </Link>
                    )}
                    {getCurrentStep(deal) === 3 && (
                      <Link to={`/report-generation?dealId=${deal.id}`} className="block">
                        <Button size="sm" className="w-full">
                          <FileBarChart className="w-4 h-4 mr-2" />
                          {hasCompletedReport(deal) ? "View Report" : "Generate Report"}
                        </Button>
                      </Link>
                    )}
                    {isAdmin && (
                      <Link to={`/register-deal?dealId=${deal.id}`} className="block">
                        <Button variant="outline" size="sm" className="w-full">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Deal
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle>Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Documents</span>
                      <span className="font-medium">{deal._count?.documents || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Reports</span>
                      <span className="font-medium">{deal._count?.reports || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Suppliers</span>
                      <span className="font-medium">{deal.suppliers?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Notes</span>
                      <span className="font-medium">{deal.notes?.length || 0}</span>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Created</span>
                        <span className="font-medium text-sm">
                          {dealService.formatDate(deal.createdAt)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Updated</span>
                        <span className="font-medium text-sm">
                          {dealService.formatDate(deal.updatedAt)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            {/* END overview */}
          </TabsContent>

          {/* Requisition */}
          <TabsContent value="requisition" className="mt-6">
            {deal.customRequisitionData?.categories && (
              <div className="space-y-6">
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="flex items-center gap-2 text-xl">
                      <List className="w-5 h-5" />
                      Custom Requisition Categories
                    </CardTitle>
                   <Button
                  onClick={exportRequisitionListToPDF}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
              >
           <Download className="w-4 h-4" />
            Export PDF
           </Button>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-6">
                      {deal.customRequisitionData.categories.map((category, categoryIndex) => (
                        <div key={categoryIndex} className="border rounded-lg p-4">
                          <h3 className="text-lg font-semibold mb-4 text-primary">
                            {category.title || `Category ${categoryIndex + 1}`}
                          </h3>
                          {Array.isArray(category.items) && category.items.length > 0 ? (
                            <div className="space-y-2">
                              <h4 className="text-xs uppercase tracking-wide text-muted-foreground">
                                Required Documents ({category.items.length})
                              </h4>
                              <ul className="space-y-2 mt-2">
                                {category.items.map((rawItem: any, itemIndex: number) => {
                                  const item = typeof rawItem === "string" ? { name: rawItem } : rawItem;
                                  const {
                                    id,
                                    name,
                                    description,
                                    isRequired,
                                    acceptedFormats,
                                    examples,
                                  }: {
                                    id?: string;
                                    name?: string;
                                    description?: string;
                                    isRequired?: boolean;
                                    acceptedFormats?: string[];
                                    examples?: string[];
                                  } = item || {};

                                  return (
                                    <li
                                      key={id ?? `${categoryIndex}-${itemIndex}`}
                                      className="p-3 rounded border bg-muted/30"
                                    >
                                      <div className="flex items-start gap-3">
                                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">
                                              {name ?? "Untitled item"}
                                            </span>
                                            {/* {typeof isRequired === "boolean" && (
                                              <span
                                                className={`text-[10px] px-2 py-0.5 rounded border ${
                                                  isRequired
                                                    ? "bg-red-50 text-red-700 border-red-200"
                                                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                }`}
                                              >
                                                {isRequired ? "Required" : "Optional"}
                                              </span>
                                            )} */}
                                          </div>

                                          {description && (
                                            <p className="text-xs text-muted-foreground mt-1">{description}</p>
                                          )}

                                          {Array.isArray(acceptedFormats) && acceptedFormats.length > 0 && (
                                            <div className="mt-2 text-xs">
                                              <span className="text-muted-foreground">Accepted: </span>
                                              <span className="font-medium">
                                                {acceptedFormats.join(", ")}
                                              </span>
                                            </div>
                                          )}

                                          {Array.isArray(examples) && examples.length > 0 && (
                                            <div className="mt-2">
                                              <div className="text-[11px] text-muted-foreground mb-1">Examples:</div>
                                              <ul className="list-disc pl-5 text-xs space-y-1">
                                                {examples.map((ex, exIdx) => (
                                                  <li key={`${id ?? itemIndex}-ex-${exIdx}`}>{ex}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">
                              No items specified for this category.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {(!deal.customRequisitionData?.categories ||
              deal.customRequisitionData.categories.length === 0) && (
              <Card className="border-border/60 shadow-sm">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <List className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Custom Requisition Data</h3>
                  <p className="text-muted-foreground text-center mb-4">
                    This deal doesn't have any custom requisition categories configured yet.
                  </p>
                  {isAdmin && (
                    <Link to={`/register-deal?dealId=${deal.id}`}>
                      <Button>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Deal to Add Categories
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
          {/* Assignees */}
          {isAdmin && (
            <TabsContent value="assignees" className="mt-6">
  {(() => {
    const personaRows = Array.isArray((deal as any).dealPersonas) ? (deal as any).dealPersonas : [];

    const personaNameByType = new Map<string, string>();
    if (Array.isArray((deal as any).selectedPersonasWithNames)) {
      for (const p of (deal as any).selectedPersonasWithNames) {
        if (p?.type) personaNameByType.set(p.type, p?.name || p.type);
      }
    }
    const pretty = (t?: string) =>
      (t || "")
        .toLowerCase()
        .split("_")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");

    const personaLabel = (type?: string) =>
      (type && personaNameByType.get(type)) || pretty(type);

    type ReviewerCard = { id: string; name: string; email?: string | null; role: "REVIEWER"; personas: string[] };

    const reviewerAssignments = personaRows
      .filter((dp: any) => (dp?.role ?? "REVIEWER") === "REVIEWER")
      .map((dp: any) => ({
        userId: dp?.userId ?? dp?.user?.id ?? "unknown",
        user: dp?.user,
        personaType: dp?.personaType, 
      }));

    const reviewersMap = new Map<string, ReviewerCard>();
    for (const ra of reviewerAssignments) {
      const u = ra.user ?? {};
      const id = ra.userId;
      const name =
        [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
        u?.email ||
        ra.userId ||
        "Unknown user";
      const email = u?.email ?? null;
      const label = personaLabel(ra.personaType);

      if (!reviewersMap.has(id)) {
        reviewersMap.set(id, { id, name, email, role: "REVIEWER", personas: label ? [label] : [] });
      } else if (label) {
        const card = reviewersMap.get(id)!;
        if (!card.personas.includes(label)) card.personas.push(label);
      }
    }
    const reviewerList = Array.from(reviewersMap.values());

    const analystsRaw = (deal as any).analysts ?? (deal as any).dealAnalysts ?? [];
    const analystList = (Array.isArray(analystsRaw) ? analystsRaw : []).map((a: any) => {
      const u = a?.user ?? a;
      return {
        id: a?.userId ?? u?.id ?? "unknown",
        name:
          [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
          u?.email ||
          a?.userId ||
          "Unknown user",
        email: u?.email ?? null,
        role: "ANALYST" as const,
      };
    });

    const viewersRaw = (deal as any).viewers ?? (deal as any).dealViewers ?? [];
    const viewerList = (Array.isArray(viewersRaw) ? viewersRaw : []).map((v: any) => {
      const u = v?.user ?? v;
      return {
        id: v?.userId ?? u?.id ?? "unknown",
        name:
          [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
          u?.email ||
          v?.userId ||
          "Unknown user",
        email: u?.email ?? null,
        role: "VIEWER" as const,
      };
    });

    const sections: Array<{
      title: string;
      items: any[];
      badgeClass: string;
      showPersonaChips?: boolean;
    }> = [
      { title: "Analysts", items: analystList, badgeClass: "bg-blue-100 text-blue-700 border-blue-200" },
      { title: "Reviewers", items: reviewerList, badgeClass: "bg-amber-100 text-amber-700 border-amber-200", showPersonaChips: true },
      { title: "Viewers", items: viewerList, badgeClass: "bg-slate-100 text-slate-700 border-slate-200" },
    ];

    const total = analystList.length + reviewerList.length + viewerList.length;

    if (total === 0) {
      return (
        <Card className="border-dashed border-border/70 shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-1">No users assigned</h3>
            <p className="text-muted-foreground text-center mb-4">
              Assign analysts, reviewers, or viewers to get this deal moving.
            </p>
            <Link to={`/register-deal?dealId=${deal.id}`}>
              <Button size="sm">
                <Edit className="w-4 h-4 mr-2" />
                Assign Users
              </Button>
            </Link>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Users className="w-5 h-5" />
            Assignees ({total})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-8">
          {sections.map((sec) => (
            <section key={sec.title} className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                  {sec.title}
                </h3>
                <Badge variant="outline" className="px-2 py-0.5">{sec.items.length}</Badge>
              </div>

              {sec.items.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                  No {sec.title.toLowerCase()} assigned.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {sec.items.map((u: any) => (
                    <div key={`${sec.title}-${u.id}`} className="border rounded-xl p-4 bg-card/60 hover:bg-card transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="leading-tight">
                          <p className="font-medium">{u.name}</p>
                          {u.email && <p className="text-xs text-muted-foreground">{u.email}</p>}
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${sec.badgeClass}`}>
                          {u.role}
                        </span>
                      </div>

                      {sec.showPersonaChips && Array.isArray(u.personas) && u.personas.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {u.personas.map((pLabel: string, idx: number) => (
                            <span
                              key={`${u.id}-p-${idx}`}
                              className="text-[10px] leading-4 px-2 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200"
                              title={pLabel}
                            >
                              {pLabel}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </CardContent>
      </Card>
    );
  })()}
</TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ViewDeal;
